/**
 * AetherLite API client — wrapper de fetch con base URL configurable y JWT.
 *
 * Resolución de BASE_URL (en orden):
 *   1. window.AETHERLITE_API → seteado en index.html (producción)
 *   2. import.meta.env.VITE_API_BASE_URL → para overrides en dev (.env.local)
 *   3. Fallback hardcodeado a aethercoach.nilabs.co
 *
 * Auth model (DIFERENTE A PANINI):
 *   - AetherLite usa JWT puro de 30 días emitido por POST /aetherlite/auth/google.
 *   - NO hay refresh_token httpOnly cookie ni endpoint /auth/refresh.
 *   - Si un request devuelve 401 (token expirado, revocado, server reset…),
 *     disparamos `aetherlite-auth-expired` y el AuthProvider hace logout +
 *     redirect a la pantalla de login Google.
 *
 * Token persistence:
 *   - localStorage["aetherlite.access_token"]
 */

function resolveBaseUrl(): string {
  if (typeof window !== "undefined" && window.AETHERLITE_API) {
    return window.AETHERLITE_API.replace(/\/+$/, "");
  }
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL.replace(/\/+$/, "");
  }
  return "https://aethercoach.nilabs.co";
}

const BASE_URL = resolveBaseUrl();
const TOKEN_KEY = "aetherlite.access_token";

/** Evento custom disparado cuando un request devuelve 401 con token presente.
 *  AuthProvider escucha este evento para forzar logout + login screen. */
export const AUTH_EXPIRED_EVENT = "aetherlite-auth-expired";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  // Limpieza defensiva de keys legacy del proyecto vanilla viejo (CoachKit).
  localStorage.removeItem("coachkit.access_token");
  localStorage.removeItem("aetherlite.refresh_token");
  localStorage.removeItem("panini.access_token");
}

export function isApiConfigured(): boolean {
  return Boolean(BASE_URL);
}

export function apiBaseUrl(): string {
  return BASE_URL;
}

/**
 * Absolutiza una URL de /uploads (gifs de ejercicios, etc.). En el catálogo
 * algunas `video_url` quedaron relativas (`/uploads/exercises/0015.gif`); en el
 * link público (dominio aetherlite.nilabs.co) un `<img src="/uploads/...">`
 * apunta al dominio equivocado y da 404. Acá la anclamos al backend.
 */
export function absUploadUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/")) return BASE_URL + url;
  return url;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public detail?: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Endpoint principal de fetch contra el backend AetherLite.
 *
 * Convención: el path empieza por "/" y se concatena al BASE_URL. Ejemplos:
 *   apiFetch("/aetherlite/auth/me")
 *   apiFetch("/aetherlite/plans", { method: "POST", body: JSON.stringify(...) })
 */
export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (!BASE_URL) throw new ApiError(0, "API base URL no configurada");

  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body && !headers.has("Content-Type") && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers,
  });

  // 401 con token presente => token expiró o fue revocado. Limpiamos y avisamos.
  if (res.status === 401 && token) {
    clearTokens();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
    }
  }

  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      try { detail = await res.text(); } catch { detail = null; }
    }
    const msg =
      detail && typeof detail === "object" && "detail" in detail
        ? String((detail as { detail: unknown }).detail)
        : res.statusText || `HTTP ${res.status}`;
    throw new ApiError(res.status, msg, detail);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Alias retrocompatible — algunos módulos legacy importan `api`. */
export const api = apiFetch;
